/**
 * Funciones tipadas, una por endpoint.
 *
 * Los componentes nunca llaman a axios directamente: hablan con estas
 * funciones a través de los hooks de React Query, así la forma de cada
 * petición vive en un solo sitio.
 */

import { api } from '@/api/client';
import type {
  AdminDashboard,
  AnswerFeedback,
  AnswerOption,
  CurrentQuestion,
  DashboardSummary,
  GameMode,
  GameResult,
  GameSessionState,
  LeaderboardEntry,
  Level,
  LevelWithStats,
  Page,
  Question,
  TokenResponse,
  UploadResponse,
  UserAdminView,
  UserMe,
  UserStats,
} from '@/types/api';

// --------------------------------------------------------------------------- //
// Autenticación
// --------------------------------------------------------------------------- //
export interface LoginPayload {
  username: string;
  password: string;
}

export interface RegisterPayload {
  username: string;
  email: string;
  password: string;
  confirm_password: string;
  phone?: string | null;
}

export const authApi = {
  login: (payload: LoginPayload) =>
    api.post<TokenResponse>('/auth/login', payload).then((r) => r.data),

  register: (payload: RegisterPayload) =>
    api.post<UserMe>('/auth/register', payload).then((r) => r.data),

  refresh: () => api.post<TokenResponse>('/auth/refresh').then((r) => r.data),

  logout: () => api.post('/auth/logout').then(() => undefined),

  me: () => api.get<UserMe>('/auth/me').then((r) => r.data),

  changePassword: (payload: { current_password: string; new_password: string }) =>
    api.post('/auth/change-password', payload).then(() => undefined),

  updateProfile: (payload: { email?: string; phone?: string | null }) =>
    api.patch<UserMe>('/me', payload).then((r) => r.data),
};

// --------------------------------------------------------------------------- //
// Juego
// --------------------------------------------------------------------------- //
export const gameApi = {
  dashboard: () => api.get<DashboardSummary>('/dashboard').then((r) => r.data),

  start: (payload: { mode: GameMode; level_number?: number }) =>
    api.post<GameSessionState>('/games', payload).then((r) => r.data),

  active: () => api.get<GameSessionState | null>('/games/active').then((r) => r.data),

  question: (sessionId: number) =>
    api.get<CurrentQuestion>(`/games/${sessionId}/question`).then((r) => r.data),

  answer: (sessionId: number, payload: { question_id: number; answer: AnswerOption }) =>
    api.post<AnswerFeedback>(`/games/${sessionId}/answer`, payload).then((r) => r.data),

  finish: (sessionId: number) =>
    api.post<GameResult>(`/games/${sessionId}/finish`).then((r) => r.data),

  abandon: (sessionId: number) => api.post(`/games/${sessionId}/abandon`).then(() => undefined),

  history: (params: { page?: number; page_size?: number } = {}) =>
    api.get<Page<GameSessionState>>('/games', { params }).then((r) => r.data),

  leaderboard: (params: { level?: number; period?: string; limit?: number } = {}) =>
    api.get<LeaderboardEntry[]>('/leaderboard', { params }).then((r) => r.data),

  stats: () => api.get<UserStats>('/stats').then((r) => r.data),
};

// --------------------------------------------------------------------------- //
// Niveles
// --------------------------------------------------------------------------- //
export interface LevelPayload {
  level_number: number;
  name: string;
  description?: string | null;
  min_score: number;
}

export const levelsApi = {
  list: () => api.get<Level[]>('/levels').then((r) => r.data),

  listWithStats: () => api.get<LevelWithStats[]>('/levels/stats').then((r) => r.data),

  create: (payload: LevelPayload) => api.post<Level>('/levels', payload).then((r) => r.data),

  update: (id: number, payload: Partial<LevelPayload>) =>
    api.patch<Level>(`/levels/${id}`, payload).then((r) => r.data),

  remove: (id: number) => api.delete(`/levels/${id}`).then(() => undefined),
};

// --------------------------------------------------------------------------- //
// Preguntas
// --------------------------------------------------------------------------- //
export interface QuestionPayload {
  level_id: number;
  question_text: string;
  option_a: string | null;
  option_b: string | null;
  option_c: string | null;
  option_d: string | null;
  option_a_image?: string | null;
  option_b_image?: string | null;
  option_c_image?: string | null;
  option_d_image?: string | null;
  option_a_audio?: string | null;
  option_b_audio?: string | null;
  option_c_audio?: string | null;
  option_d_audio?: string | null;
  correct_answer: AnswerOption;
  explanation?: string | null;
  points: number;
  audio_file?: string | null;
  image_file?: string | null;
}

export const questionsApi = {
  list: (params: { page?: number; page_size?: number; level_id?: number; search?: string } = {}) =>
    api.get<Page<Question>>('/questions', { params }).then((r) => r.data),

  get: (id: number) => api.get<Question>(`/questions/${id}`).then((r) => r.data),

  create: (payload: QuestionPayload) =>
    api.post<Question>('/questions', payload).then((r) => r.data),

  update: (id: number, payload: Partial<QuestionPayload>) =>
    api.patch<Question>(`/questions/${id}`, payload).then((r) => r.data),

  remove: (id: number) => api.delete(`/questions/${id}`).then(() => undefined),

  upload: (kind: 'audio' | 'image', file: File) => {
    const form = new FormData();
    form.append('file', file);
    // Sin Content-Type explícito: el navegador añade el boundary de multipart.
    return api
      .post<UploadResponse>(`/questions/upload/${kind}`, form, {
        headers: { 'Content-Type': undefined },
      })
      .then((r) => r.data);
  },
};

// --------------------------------------------------------------------------- //
// Administración
// --------------------------------------------------------------------------- //
export interface UserCreatePayload {
  username: string;
  email: string;
  password: string;
  phone?: string | null;
  is_admin: boolean;
}

export interface UserUpdatePayload {
  email?: string;
  phone?: string | null;
  level?: number;
  score?: number;
  is_admin?: boolean;
  is_active?: boolean;
}

export const adminApi = {
  dashboard: () => api.get<AdminDashboard>('/admin/dashboard').then((r) => r.data),

  listUsers: (
    params: {
      page?: number;
      page_size?: number;
      search?: string;
      include_admins?: boolean;
      is_active?: boolean;
    } = {},
  ) => api.get<Page<UserAdminView>>('/users', { params }).then((r) => r.data),

  createUser: (payload: UserCreatePayload) =>
    api.post<UserAdminView>('/users', payload).then((r) => r.data),

  updateUser: (id: number, payload: UserUpdatePayload) =>
    api.patch<UserAdminView>(`/users/${id}`, payload).then((r) => r.data),

  setUserActive: (id: number, active: boolean) =>
    api
      .post<UserAdminView>(`/users/${id}/${active ? 'activate' : 'deactivate'}`)
      .then((r) => r.data),

  deleteUser: (id: number) => api.delete(`/users/${id}`).then(() => undefined),
};
